import { useCallback, useEffect, useRef, useState } from 'react'
import HTMLFlipBook from 'react-pageflip'
import { motion, useReducedMotion } from 'framer-motion'
import type { MenuPage } from '@/content/printMenu/types'
import { formatBookletPageLabel, getBookletVisibleRange } from '@/lib/bookletPageRange'
import { computeFlipPageSize, MENU_PAGE_ASPECT } from '@/lib/menuPageMetrics'
import { smoothEase } from '@/lib/motion'
import { useMediaQuery } from '@/lib/useMediaQuery'
import { cn } from '@/lib/utils'
import { MenuFlipPage } from '@/components/printMenu/MenuFlipPage'
import { renderMenuPage } from '@/components/printMenu/renderMenuPage'

type MenuFlipBookProps = {
  pages: MenuPage[]
  totalPages: number
}

type BookOrientation = 'portrait' | 'landscape'

type FlipBookRef = {
  pageFlip: () => {
    flipNext: (corner?: 'top' | 'bottom') => void
    flipPrev: (corner?: 'top' | 'bottom') => void
    getCurrentPageIndex: () => number
    getPageCount: () => number
    getOrientation: () => BookOrientation
  }
}

export function MenuFlipBook({ pages, totalPages }: MenuFlipBookProps) {
  const reduced = useReducedMotion()
  const isNarrow = useMediaQuery('(max-width: 767px)')
  const bookRef = useRef<FlipBookRef>(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [orientation, setOrientation] = useState<BookOrientation>('landscape')
  const [size, setSize] = useState(() =>
    computeFlipPageSize({ width: window.innerWidth, height: window.innerHeight }),
  )

  const syncBookState = useCallback(() => {
    const api = bookRef.current?.pageFlip()
    if (!api) return
    setPageIndex(api.getCurrentPageIndex())
    setOrientation(api.getOrientation())
  }, [])

  useEffect(() => {
    const onResize = () => {
      setSize(computeFlipPageSize({ width: window.innerWidth, height: window.innerHeight }))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const flipNext = useCallback(() => {
    bookRef.current?.pageFlip().flipNext()
  }, [])

  const flipPrev = useCallback(() => {
    bookRef.current?.pageFlip().flipPrev()
  }, [])

  useEffect(() => {
    if (reduced) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        flipNext()
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        flipPrev()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [flipNext, flipPrev, reduced])

  const onFlip = useCallback(
    (event: { data: number }) => {
      setPageIndex(event.data)
      syncBookState()
    },
    [syncBookState],
  )

  const onInit = useCallback(() => {
    syncBookState()
  }, [syncBookState])

  const onChangeOrientation = useCallback(() => {
    syncBookState()
  }, [syncBookState])

  const visibleRange = getBookletVisibleRange(pageIndex, totalPages, orientation)
  const pageLabel = formatBookletPageLabel(visibleRange, totalPages)
  const atStart = pageIndex <= 0
  const atEnd = pageIndex >= pages.length - 1
  const isSpread = visibleRange.start !== visibleRange.end

  return (
    <div className="menu-flip-book">
      <motion.div
        className="menu-flip-book__stage"
        initial={reduced ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduced ? 0 : 0.5, ease: smoothEase }}
      >
        <HTMLFlipBook
          key={isNarrow ? 'portrait' : 'landscape'}
          ref={bookRef}
          className="menu-flip-book__canvas"
          style={{}}
          width={size.width}
          height={size.height}
          size="fixed"
          minWidth={280}
          maxWidth={size.width}
          minHeight={Math.floor(280 * MENU_PAGE_ASPECT)}
          maxHeight={size.height}
          drawShadow
          flippingTime={720}
          usePortrait={isNarrow}
          useMouseEvents
          showCover
          showPageCorners
          maxShadowOpacity={0.5}
          mobileScrollSupport={false}
          clickEventForward
          autoSize={false}
          startPage={0}
          startZIndex={0}
          swipeDistance={24}
          disableFlipByClick={false}
          onFlip={onFlip}
          onInit={onInit}
          onChangeOrientation={onChangeOrientation}
        >
          {pages.map((page, index) => (
            <MenuFlipPage key={`flip-${index + 1}`} pageWidthPx={size.width}>
              {renderMenuPage(page, index + 1, totalPages)}
            </MenuFlipPage>
          ))}
        </HTMLFlipBook>
      </motion.div>

      <motion.div
        className="menu-flip-book__controls"
        initial={reduced ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: reduced ? 0 : 0.2, duration: 0.35 }}
      >
        <button
          type="button"
          onClick={flipPrev}
          disabled={atStart}
          className={cn(
            'menu-flip-book__nav',
            atStart && 'menu-flip-book__nav--disabled',
          )}
          aria-label="Previous page"
        >
          Previous
        </button>
        <p className="menu-flip-book__indicator" aria-live="polite">
          <span className="font-semibold text-ink">{pageLabel}</span>
          {isSpread ? (
            <span className="mt-0.5 block text-[11px] font-normal text-muted">
              Double-sided spread
            </span>
          ) : null}
        </p>
        <button
          type="button"
          onClick={flipNext}
          disabled={atEnd}
          className={cn(
            'menu-flip-book__nav',
            atEnd && 'menu-flip-book__nav--disabled',
          )}
          aria-label="Next page"
        >
          Next
        </button>
      </motion.div>
      <p className="menu-flip-book__hint font-sans text-xs text-muted">
        {isNarrow
          ? 'Flip one page at a time — drag a corner or use arrow keys'
          : 'Open booklet: two pages per spread · drag corners or use arrow keys'}
      </p>
    </div>
  )
}
